// src/pages/MyApplicationsPage.tsx

import { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Grid, Skeleton, Card, CardContent, CardActions,
  Button, Chip, Backdrop, useTheme, useMediaQuery
} from '@mui/material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import DevicesIcon from '@mui/icons-material/Devices';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { db } from '../firebase';
import { Application } from './admin/types/application';
import PhoneVerificationPanel from './components/PhoneVerificationPanel';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { motion } from 'framer-motion';
import STATUS_COLORS from '../constants/statusColors';

const gradientBg = 'linear-gradient(135deg, #e0f2fe 0%, #f8fafc 45%, #e0e7ff 100%)';

const MyApplicationsPage = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<{ name: string; phone: string }>({ name: '', phone: '' });
  const [blocking, setBlocking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const imp_uid = params.get('imp_uid');
    const success = params.get('success') === 'true';

    if (success && imp_uid) {
      fetchCertificationInfo(imp_uid);
    } else {
      const saved = sessionStorage.getItem('auth_user');
      if (saved) {
        const { name, phone } = JSON.parse(saved);
        setUserInfo({ name, phone });
        setIsAuthenticated(true);
      }
    }
  }, [location.search]);

  const fetchCertificationInfo = async (imp_uid: string) => {
    try {
      setBlocking(true);
      const res = await fetch('https://us-central1-refurb-buy.cloudfunctions.net/getCertificationInfo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imp_uid }),
      });
      const data = await res.json();
      const { name, phone } = data;
      sessionStorage.setItem('auth_user', JSON.stringify({ name, phone }));
      setUserInfo({ name, phone });
      setIsAuthenticated(true);
    } catch (e) {
      console.error('인증정보 불러오기 실패', e);
    } finally {
      setBlocking(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !userInfo.phone) return;
    setLoading(true);
    const fetchApplications = async () => {
      const q = query(
        collection(db, 'applications'),
        where('phone_number', '==', userInfo.phone),
        orderBy('created_at', 'desc')
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Application[];
      setApplications(data);
      setLoading(false);
    };
    fetchApplications();
  }, [isAuthenticated, userInfo.phone]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 3, md: 6 },
        background: gradientBg,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* 상단 홈 버튼 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          maxWidth: 1100,    
          mx: 'auto',
          pt: { xs: 1, md: 2.5 },
          pb: { xs: 1.2, md: 2.5 },
          zIndex: 5
        }}
      >
        <Button
          variant="contained"
          startIcon={<HomeRoundedIcon sx={{ fontSize: 28 }} />}
          onClick={() => navigate('/')}
          sx={{
            borderRadius: 99,
            background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: { xs: 14, md: 17 },
            px: { xs: 2, md: 3.2 },
            py: { xs: 1, md: 1.2 },
            boxShadow: '0 2px 16px rgba(14,165,233,0.10)',
            minHeight: 44,
            minWidth: 0,
            textTransform: 'none',
            mr: 1.5,
            transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
            '&:hover': {
              background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 6px 20px 0 rgba(139,92,246,0.13)',
              filter: 'brightness(1.07)'
            }
          }}
          component={motion.button}
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.025 }}
        >
          뒤로가기
        </Button>
      </Box>
      

      <Backdrop open={blocking} sx={{ zIndex: 1300, color: '#2563eb', bgcolor: 'rgba(255,255,255,0.58)' }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Box mt={2}>인증 또는 조회 중입니다...</Box>
        </Box>
      </Backdrop>

      <Box
        sx={{
          maxWidth: 1140,
          mx: 'auto',
          zIndex: 2,
          position: 'relative',
        }}
      >
        {!isAuthenticated ? (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            sx={{
              maxWidth: 420,
              mx: 'auto',
              mt: 8,
              mb: 8,
              borderRadius: 5,
              background: 'rgba(255,255,255,0.87)',
              boxShadow: '0 8px 36px 0 rgba(14,165,233,0.11)',
              p: { xs: 2.5, sm: 4 },
              backdropFilter: 'blur(14px)',
            }}
          >
            <PhoneVerificationPanel
              onVerified={({ name, phone }) => {
                sessionStorage.setItem('auth_user', JSON.stringify({ name, phone }));
                setUserInfo({ name, phone });
                setIsAuthenticated(true);
              }}
              blocking={blocking}
              setBlocking={setBlocking}
            />
          </Box>
        ) : (
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h4"}
              gutterBottom
              fontWeight={900}
              align="center"
              
              component={motion.div}
              initial={{ opacity: 0, y: -28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
            >
              📋 내 중고기기 신청 내역
            </Typography>
            {loading ? (
              <Grid container spacing={2} mt={1}>
                {[...Array(3)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rounded" height={180} sx={{ borderRadius: 4.5 }} />
                  </Grid>
                ))}
              </Grid>
            ) : applications.length === 0 ? (
              <Typography sx={{ mt: 7 }} color="text.secondary" align="center">
                신청 내역이 없습니다.
              </Typography>
            ) : (
              <Grid container spacing={3} mt={1}>
                {applications.map((app, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={app.id}>
                    <Card
                      component={motion.div}
                      initial={{ opacity: 0, y: 36 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.46, delay: 0.11 + idx * 0.07 }}
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderRadius: 5,
                        borderColor: 'rgba(131,164,246,0.23)',
                        background: 'rgba(255,255,255,0.88)',
                        boxShadow: '0 3px 28px 0 rgba(11,93,246,0.07)',
                        backdropFilter: 'blur(8px)',
                        transition: 'all 0.23s cubic-bezier(.4,2,.6,1)',
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.021)',
                          boxShadow: '0 12px 38px rgba(99,102,241,0.14)',
                          borderColor: '#6366f1',
                        },
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/my-list/${app.id}`)}
                    >
                      <CardContent sx={{ p: isMobile ? 2 : 2.7 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1.2}>
                          <DevicesIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                          <Typography variant="body2" fontWeight={700}>
                            {app.devices.map(d => `${d.deviceType} (${d.deviceModel})`).join(', ')}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <HomeIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {app.pickup_address}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={1} fontWeight={500}>
                          신청일: {app.created_at?.seconds ? new Date(app.created_at.seconds * 1000).toLocaleDateString() : '-'}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          제안금액: {app.price_offer?.toLocaleString() || 0}원
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={900}
                          sx={{
                            background: 'linear-gradient(90deg, #6366f1 10%, #0ea5e9 100%)',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                          }}
                        >
                          최종금액: {app.final_price?.toLocaleString() || 0}원
                        </Typography>
                        <Chip
                          label={app.status}
                          size="small"
                          sx={{
                            mt: 1.3,
                            borderRadius: 2.5,
                            px: 1.3,
                            fontWeight: 800,
                            fontSize: 14,
                            letterSpacing: 0.3,
                            background: STATUS_COLORS[app.status] || '#cfd8dc',
                            color: '#fff',
                            boxShadow: `0 1.5px 12px 0 ${(STATUS_COLORS[app.status] || '#bdbdbd')}19`,
                          }}
                        />
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, pt: 0 }}>
                        <Button
                          size="small"
                          endIcon={<ArrowForwardIosIcon fontSize="small" />}
                          sx={{
                            fontWeight: 700,
                            borderRadius: 3,
                            color: "#3b82f6",
                            textTransform: 'none',
                            px: 2.1,
                            py: 0.9,
                            fontSize: isMobile ? 15 : 16,
                            background: 'linear-gradient(90deg, #e0e7ff 0%, #f0f9ff 100%)',
                            boxShadow: '0 2px 10px 0 rgba(139,92,246,0.07)',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                              color: '#fff',
                            }
                          }}
                          component={motion.button}
                          whileTap={{ scale: 0.98 }}
                          whileHover={{ scale: 1.025 }}
                        >
                          자세히
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MyApplicationsPage;
